import { Select, SelectProps } from '@mantine/core';
import { useApi } from 'api/api-context';
import panic from 'errors/panic';
import { useEffect, useMemo, useState } from 'react';

const ALL_OPTIONS = [
  { value: 'revision', label: 'Revízna správa' },
  { value: 'form-service-report', label: 'Servisná správa' },
  { value: 'form-service-report-supplier', label: 'Servisná správa - Poskytovateľ č.2' },
];

type RevisionTypeSelectProps = Omit<SelectProps, 'data'> & {
  excludeRevision?: boolean;
  departmentId?: number;
  autoSelectSingleResult?: boolean;
};

/**
 * Select for revision type.
 */
export default function RevisionTypeSelect({
  excludeRevision = false,
  departmentId,
  autoSelectSingleResult = false,
  ...props
}: RevisionTypeSelectProps) {
  const { getAction } = useApi();
  const [allowedValues, setAllowedValues] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (departmentId === undefined) {
      setAllowedValues(null);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const getPermissions = getAction('AuthUserGetPermissionsInDepartment');

    getPermissions({ parameters: { departmentId: String(departmentId) } })
      .then((permissions) => {
        if (cancelled) {
          return;
        }

        const allowed: string[] = [];

        if (permissions.some(({ slug, hasPermission }) => slug.endsWith('-revisions') && hasPermission)) {
          allowed.push('revision');
        }

        for (const { value } of ALL_OPTIONS) {
          if (value !== 'revision' && permissions.some(({ slug, hasPermission }) => slug === value && hasPermission)) {
            allowed.push(value);
          }
        }

        setAllowedValues(allowed);
      })
      .catch(panic)
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [departmentId, getAction]);

  const data = useMemo(() => {
    let options = ALL_OPTIONS;

    if (excludeRevision) {
      options = options.filter((o) => o.value !== 'revision');
    }

    if (allowedValues !== null) {
      options = options.filter((o) => allowedValues.includes(o.value));
    }

    return options;
  }, [excludeRevision, allowedValues]);

  useEffect(() => {
    if (autoSelectSingleResult && data.length === 1 && !props.value) {
      props.onChange?.(data[0].value);
    }
  }, [autoSelectSingleResult, data, props.value]);

  return <Select data={data} {...props} disabled={loading || props.disabled} />;
}
